import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOFracModelDerivativeChart = ({ editMode, linearFlowStart, setLinearFlowStart, linearFlowEnd, setLinearFlowEnd }) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const currentFracModel = currentStage?.fracModel;
  const units = currentRTFO?.settings?.units || 'metric';

  const data = useMemo(() => {
    if (currentRTFO) {
      return getChartData();
    };

    function getChartData() {
      try {
        
        // add traces
        let traces = [];

        const X = [];

        const YDPPrime = [];
        currentFracModel?.falloffData?.forEach((d,i) => {
          if (d.dTFalloff > 0 && d.dPOverQPrime > 0) {
            X.push(d.dTFalloff);
            YDPPrime.push(d.dPOverQPrime * (units === 'field' ? rtfoConversionFactors.dPOverQPrime : 1));
          };
        });

        const dPPrimeTrace =  { // dp/Q' trace
          x: X,
          y: YDPPrime,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: `${currentRTFO?.currentStageName} dP/Q'`,
          line: {
            width: 2,
            color: chartColors[5],
            opacity: 1
          }
        };
        
        traces.push(
          dPPrimeTrace
        );
  
        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentFracModel?.falloffData, currentRTFO, units]);


  const layout = {
    yaxis: { // dP/Q' y axis
      autorange: true,
      linecolor: chartColors[5],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[5],
      title: {
        text: `dP/Q' (${units === 'field' ? 'psi' : 'kPa'}/${units === 'field' ? 'ft3' : 'm3'}/day/day)`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      type: 'log',
      side: 'left'
    },
    xaxis: { // dT falloff x axis
      range: [-3, 1],
      type: 'log',
      title: {
        text: 'dT Falloff (h)',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across"
    },
    hovermode: "x",
    hoverdistance: 10, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      y: -0.1
    },
    annotations: [
      {
        text: "Frac Model Derivative",
          font: {
          size: 16,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: 1.135, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: 1.07, //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      linearFlowStart && { // start linear flow annotation
        x: Math.log10(linearFlowStart),
        y: 1.01,
        xref: 'x1',
        yref: 'paper',
        text: 'Leakoff-Dominated Start',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false
      },
      linearFlowEnd && { // end linear flow annotation
        x: Math.log10(linearFlowEnd),
        y: 1.01,
        xref: 'x1',
        yref: 'paper',
        text: 'Leakoff-Dominated End',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false
      }
    ],
    shapes: [
      linearFlowStart && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: linearFlowStart,
        y0: 0,
        x1: linearFlowStart,
        y1: 1,
        line: {
          color: 'green',
          width: 2,
          dash: 'dot'
        }
      },
      linearFlowEnd && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: linearFlowEnd,
        y0: 0,
        x1: linearFlowEnd,
        y1: 1,
        line: {
          color: 'tomato',
          width: 2,
          dash: 'dot'
        }
      }
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: 0.055,
        y: 1.05,
        sizex: 0.085,
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "page",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-ISIP-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
      onClick={(e) => handlePlotClick(e)}
    />
  );

  // FUNCTIONS
  function handlePlotClick(e) {
    try {

      if (editMode === 'start') {
        const newLinearFlowStart = e.points?.find(point => point?.data?.name?.includes('dP/Q'))?.x;
        setLinearFlowStart(newLinearFlowStart);
        
      } else if (editMode === 'end') {
        const newLinearFlowEnd = e.points?.find(point => point?.data?.name?.includes('dP/Q'))?.x;
        setLinearFlowEnd(newLinearFlowEnd);

      };

    } catch (error) {
      console.log(error);
    };
  };
};
